/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useCallback, useContext, useMemo, useRef } from "react"
import type { ChangeEvent, Context, FC, KeyboardEvent, ReactElement, ReactNode, RefObject } from "react"

import DOMPurify from "dompurify"
import eq from "lodash-es/eq"
import { type UseFormReturn, useForm } from "react-hook-form"
import { useMap, useUnmount } from "react-use"
import type { StableActions } from "react-use/lib/useMap"

import { EModalId, type IModal, useModalSelector } from "@/3514/store/modal"
import {
    type TEmptyAsyncCallback,
    type TEmptyCallback,
    emptyAsyncCallback,
    emptyCallback
} from "@/shared/types/functions"

import type { TCoachingChatCreateMatrixFeedbackMutation as TMutation } from "../api"
import { ECoachingChatQueryKey } from "../config"
import {
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationIdSelector as useConversationIdSelector
} from "../store"
import {
    ECoachingChatMatrixFeedbackModalFormField as EFormField,
    type TCoachingChatMatrixFeedbackModalChartDiscreteCoords as TChartDiscreteCoords,
    type TCoachingChatMatrixFeedbackModalInputForm as TForm,
    type TCoachingChatMatrixFeedbackModalProps as TModalProps
} from "../types"
import { coachingChatMatrixFeedbackFormResolver as formResolver } from "../validation"

import { useCoachingChatContext as useChatContext } from "./coaching-chat.context"

type TModalContext = {
    chart: { onValueChanged(data: TChartDiscreteCoords): void; isSubmittable: boolean }
    form: {
        onSubmit: TEmptyCallback
        onChange(e: ChangeEvent<HTMLTextAreaElement>): void
        onKeyDown(e: KeyboardEvent): void
        isValid: boolean
        inputRef: RefObject<HTMLTextAreaElement>
    }
    mutation: TMutation
}

type TProviderProps = { children: ReactNode }

const ModalContext: Context<TModalContext> = createContext<TModalContext>({
    chart: { onValueChanged: emptyCallback, isSubmittable: false },
    form: {
        onSubmit: emptyAsyncCallback,
        onChange: emptyAsyncCallback,
        onKeyDown: emptyAsyncCallback,
        isValid: false,
        inputRef: null
    },
    mutation: null
})

function useModalContext(): TModalContext {
    const context: TModalContext = useContext(ModalContext)

    if (!context) {
        throw new Error(
            "useCoachingChatMatrixFeedbackModalContext must be used within a CoachingChatMatrixFeedbackModalProvider"
        )
    }

    return context
}

const ModalProvider: FC<TProviderProps> = ({ children }: TProviderProps): ReactElement => {
    const {
        props: { mutationHook }
    }: IModal<TModalProps> = useModalSelector<TModalProps>(EModalId.Participant_CoachingChatMatrixFeedback)

    const mutation: TMutation = mutationHook()

    const {
        entityId,
        matrix: { handleModalClose: onClose }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const conversationId: number | null = useConversationIdSelector(entityId)

    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const [discreteChartCoords, { setAll: setDiscreteChartCoords }]: [
        TChartDiscreteCoords,
        StableActions<TChartDiscreteCoords>
    ] = useMap<TChartDiscreteCoords>({ x: 0, y: 0 })

    const isChartSubmittable: boolean = useMemo(
        (): boolean => !eq(discreteChartCoords.x, 0) && !eq(discreteChartCoords.y, 0),
        [discreteChartCoords.x, discreteChartCoords.y]
    )

    const {
        setValue: setFormFieldValue,
        reset: resetForm,
        trigger: triggerForm,
        getValues: getFormValues,
        formState: { isValid: isFormValid }
    }: UseFormReturn<TForm> = useForm<TForm>({
        resolver: formResolver,
        reValidateMode: "onChange",
        criteriaMode: "all",
        defaultValues: { [EFormField.Feedback]: String("") }
    })

    const inputRef: RefObject<HTMLTextAreaElement> = useRef<HTMLTextAreaElement>(null)

    const handleFeedbackSend: TEmptyAsyncCallback = useCallback(async (): Promise<void> => {
        await mutation.mutateAsync(
            {
                conversationId,
                text: getFormValues()[EFormField.Feedback],
                data: { pleasure: discreteChartCoords.x, depth: discreteChartCoords.y }
            },
            {
                onSuccess: async (): Promise<void> => {
                    conversationDispatcher.completeMatrixFeedback()

                    await onClose(ECoachingChatQueryKey.Conversation(conversationId))
                },
                onSettled: async (): Promise<void> => resetForm()
            }
        )
    }, [conversationDispatcher, conversationId, getFormValues, mutation, onClose, resetForm, discreteChartCoords])

    const handleFormSubmit: TEmptyCallback = useCallback(
        async (): Promise<void> =>
            triggerForm().then(
                async (isValid: boolean): Promise<void> => (isValid ? void handleFeedbackSend() : void 0)
            ),
        [handleFeedbackSend, triggerForm]
    )

    const handleInputKeyDown: (e: KeyboardEvent) => void = useCallback(
        async (event: KeyboardEvent): Promise<void> => {
            if (eq(event.key, "Enter")) {
                event.preventDefault()

                setFormFieldValue(EFormField.Feedback, `${inputRef.current.value} \n`, {
                    shouldValidate: true
                })
                inputRef.current.value = `${inputRef.current.value} \n`
            }

            return void 0
        },
        [setFormFieldValue]
    )

    const handleInputChange: (e: ChangeEvent<HTMLTextAreaElement>) => void = useCallback(
        async (e: ChangeEvent<HTMLTextAreaElement>): Promise<void> => {
            const sanitizedMessage: string = DOMPurify.sanitize(e.target.value, {
                ALLOWED_TAGS: [],
                ALLOWED_ATTR: []
            })

            setFormFieldValue(EFormField.Feedback, sanitizedMessage, {
                shouldTouch: false,
                shouldValidate: true
            })
            inputRef.current.value = e.target.value
        },
        [setFormFieldValue]
    )

    const providerValue: TModalContext = useMemo(
        (): TModalContext => ({
            chart: { onValueChanged: setDiscreteChartCoords, isSubmittable: isChartSubmittable },
            form: {
                onSubmit: handleFormSubmit,
                onChange: handleInputChange,
                onKeyDown: handleInputKeyDown,
                isValid: isFormValid,
                inputRef
            },
            mutation
        }),
        [isChartSubmittable, handleFormSubmit, handleInputChange, handleInputKeyDown, isFormValid, mutation]
    )

    useUnmount((): void => {
        mutation.reset()
    })

    return <ModalContext.Provider value={providerValue}>{children}</ModalContext.Provider>
}

export {
    ModalProvider as CoachingChatMatrixFeedbackModalProvider,
    useModalContext as useCoachingChatMatrixFeedbackModalContext
}
